import {
    IonButton,
    IonCard,
    IonCardContent,
    IonCardHeader,
    IonCardTitle,
    IonIcon,
    IonItem,
    IonLabel,
    IonList,
    IonSpinner,
    useIonRouter,
} from "@ionic/react";
import {
    alertCircle,
    checkmarkCircle,
    logoAppleAppstore,
    logoGooglePlaystore,
} from "ionicons/icons";
import { useTranslation } from "react-i18next";
import { FC, useEffect, useRef, useState } from "react";
import { SelectedDevice } from "../redux/models/refsixState";
import { GarminSDK, IQDevice } from "capacitor-garmin-sdk";
import { useSelector } from "react-redux";
import { RootState } from "../redux/store";
import {
    isGarminBTCompatible,
    openConnectMobile,
    openStoreForConnectMobile,
    updateSelectedDevice,
} from "../services/watch/GarminWatchHelper";
import { isIos } from "../services/platformDetection";
import { GARMIN_FULL_APP_INFO } from "../constants/GarminConstants";

export type GarminWatchStatusProps = {
    device?: IQDevice | SelectedDevice;
};

type GarminWatchStatusRowProps = {
    status: "success" | "warning" | "loading";
    title: string;
    message?: string[];
    button?: {
        text: string;
        icon?: string;
        onClick: () => void;
    };
    loadingDelay?: number;
};

type RowProps = {
    config: GarminWatchStatusRowProps;
};

export const GarminWatchStatusRow: FC<RowProps> = ({ config }) => {
    const { status, title, message, button, loadingDelay } = config;
    const [delayComplete, setDelayComplete] = useState(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        timeoutRef.current = setTimeout(() => {
            // setCurrentStatus(status);
            setDelayComplete(true);
        }, loadingDelay || 0); // Set duration in milliseconds

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [status]);

    return (
        <IonItem>
            {(!delayComplete || (delayComplete && status === "loading")) && (
                <IonSpinner
                    slot={"start"}
                    style={{
                        width: 25,
                        height: 25,
                    }}
                    name="circular"
                ></IonSpinner>
            )}
            {delayComplete && status !== "loading" && (
                <IonIcon
                    style={{
                        fontSize: 25,
                    }}
                    icon={status === "success" ? checkmarkCircle : alertCircle}
                    color={status === "success" ? "primary" : "warning"}
                    slot={"start"}
                ></IonIcon>
            )}
            <IonLabel
                style={{
                    fontSize: 18,
                    "--background": "transparent",
                }}
            >
                <span style={{ fontWeight: "bold" }}>{title}</span>

                {delayComplete &&
                    message &&
                    message.length &&
                    message.map((messageLine, index) => (
                        <p
                            key={index}
                            style={{ marginTop: 5 }}
                            className={"watch-status-description"}
                        >
                            {messageLine}
                        </p>
                    ))}
                {delayComplete && button && (
                    <IonButton size="small" onClick={button.onClick}>
                        {button.icon && (
                            <IonIcon slot="start" icon={button.icon}></IonIcon>
                        )}
                        {button.text}
                    </IonButton>
                )}
            </IonLabel>
        </IonItem>
    );
};

/*
 * This component is used to display the status of the Garmin watch and get
 * the user to take the required actions. Full devices only
 * 1. connectIQInstalled: Check if the Connect IQ app is installed on the phone
 * 2. watchConnected: Check if the watch is connected to the phone
 * 3. watchAppInstalled: Check if the watch app is installed on the watch
 * 4. watchAppUpdated: Check if the watch app has a version that supports BT
 */
export const GarminWatchStatus: FC<GarminWatchStatusProps> = ({ device }) => {
    const { t } = useTranslation();
    const garminWatches = useSelector(
        (state: RootState) => state.garminWatches
    );
    const selectedGarmin = useSelector(
        (state: RootState) => state.garminWatches?.selectedDevice
    );
    const [openedConnectIQ, setOpenedConnectIQ] = useState(false);
    const [intervalHandle, setIntervalHandle] = useState<any>(null);

    const [appsInstalledRowConfig, setAppsInstalledRowConfig] =
        useState<GarminWatchStatusRowProps>({
            status: "loading",
            title: t("garminWatches.garminAppsInstalled"),
        });
    const [watchConnectedRowConfig, setWatchConnectedRowConfig] =
        useState<GarminWatchStatusRowProps>({
            status: "loading",
            title: t("garminWatches.watchConnected"),
        });
    const [watchAppInstalledRowConfig, setWatchAppInstalledRowConfig] =
        useState<GarminWatchStatusRowProps>({
            status: "loading",
            title: t("garminWatches.appInstalledOnWatch"),
        });

    const route = useIonRouter();

    const [checkCount, setCheckCount] = useState(0);
    const CHECK_INTERVAL = 5000;

    const takingAges = (checkCount: number) => {
        return checkCount > 6;
    };

    const getDownloadMessage = () => {
        // if more that 30 seconds have passed and the app is still not installed
        if (selectedGarmin && openedConnectIQ && takingAges(checkCount)) {
            return [
                t("garminWatches.syncAfterInstall"),
                t("garminWatches.waitingForInstall"),
                t("garminWatches.canProceedWhilstInstalling"),
            ];
        }
        if (selectedGarmin && openedConnectIQ) {
            return [
                t("garminWatches.syncAfterInstall"),
                t("garminWatches.waitingForInstall"),
            ];
        }

        if (selectedGarmin && !selectedGarmin.hasAppInstalled) {
            return [t("garminWatches.downloadWatchAppMessage")];
        } else if (
            selectedGarmin &&
            selectedGarmin.hasAppInstalled &&
            !selectedGarmin.isBTCompatible
        ) {
            return [t("garminWatches.updateWatchAppMessage")];
        }
        return [];
    };

    /*
     * Step 1 - contents for the apps installed row
     */
    const appsInstalledRow = (): GarminWatchStatusRowProps => {
        let row: GarminWatchStatusRowProps = {
            title: t("garminWatches.garminAppsInstalled"),
            status: "success",
            loadingDelay: 2000,
        };

        // If the app is installed we can skip this check
        if (selectedGarmin && selectedGarmin.hasAppInstalled) {
            return row;
        }

        // Garmin Connect is not installed - this shouldn't be possible

        if (garminWatches && !garminWatches.isConnectInstalled) {
            return {
                title: t("garminWatches.garminAppsInstalled"),
                status: "warning",
                message: [t("garminWatches.connectNotInstalled")],
                button: {
                    text: t("general.installApp", {
                        appName: "Garmin Connect",
                    }),
                    icon: isIos() ? logoAppleAppstore : logoGooglePlaystore,
                    onClick: () => {
                        console.log("Open App Store for Connect IQ");
                        openStoreForConnectMobile();
                    },
                },
                loadingDelay: 2000,
            };
        }

        // Check Connect IQ is installed - required to install REFSIX
        // if (garminWatches && !garminWatches.isConnectIQInstalled) {
        //     return {
        //         title: t("garminWatches.garminAppsInstalled"),
        //         status: "warning",
        //         message: [t("garminWatches.connectIqNotInstalled")],
        //         button: {
        //             text: t("general.installApp", { appName: "Connect IQ" }),
        //             icon: isIos() ? logoAppleAppstore : logoGooglePlaystore,
        //             onClick: () => {
        //                 // TODO: implement
        //                 openStoreForConnectIQ();
        //             },
        //         },
        //         loadingDelay: 2000,
        //     };
        // }

        return row;
    };

    // 2 - check watch is connected
    const watchConnectedRow = (): GarminWatchStatusRowProps => {
        let row: GarminWatchStatusRowProps = {
            title: t("garminWatches.watchConnected"),
            status: "success",
            loadingDelay: 3000,
        };

        // If the app is installed we can skip this check
        if (selectedGarmin && selectedGarmin.hasAppInstalled) {
            return row;
        }

        // if previous step is in error then don't also show this as an error
        if (appsInstalledRowConfig.status !== "success") {
            return {
                ...row,
                status: "loading",
            };
        }

        if (
            garminWatches &&
            garminWatches.isConnectInstalled &&
            !selectedGarmin?.isConnected
        ) {
            return {
                title: t("garminWatches.watchConnected"),
                status: "warning",
                message: [t("garminWatches.watchNotConnected")],
                button: {
                    text: t("general.openApp", { appName: "Connect IQ" }),
                    onClick: () => {
                        openConnectMobile().catch((e) => {});
                    },
                },
                loadingDelay: 3000,
            };
        }

        return row;
    };

    // 3 check refsix app is installed on watch
    const watchAppInstalledRow = (): GarminWatchStatusRowProps => {
        let row: GarminWatchStatusRowProps = {
            title: t("garminWatches.appInstalledOnWatch"),
            status: "success",
            loadingDelay: 4000,
        };

        // if previous step is in error then don't also show this as an error
        if (
            selectedGarmin &&
            !selectedGarmin.hasAppInstalled &&
            (appsInstalledRowConfig.status !== "success" ||
                watchConnectedRowConfig.status !== "success")
        ) {
            return {
                ...row,
                status: "loading",
            };
        }

        if (
            selectedGarmin &&
            (!selectedGarmin.hasAppInstalled || !selectedGarmin.isBTCompatible)
        ) {
            return {
                title: t("garminWatches.appInstalledOnWatch"),
                status: openedConnectIQ ? "loading" : "warning",
                message: getDownloadMessage(),
                button: {
                    text: t("garminWatches.downloadWatchApp"),
                    onClick: async () => {
                        await GarminSDK.showConnectIQStoreForApp({
                            device: selectedGarmin,
                            app: GARMIN_FULL_APP_INFO,
                        });
                        // listen for the app to be installed
                        setOpenedConnectIQ(true);
                    },
                },
                loadingDelay: 4000,
            };
        }

        return row;
    };

    useEffect(() => {
        if (
            selectedGarmin &&
            (!selectedGarmin.hasAppInstalled ||
                !selectedGarmin.isBTCompatible) &&
            !intervalHandle
        ) {
            // Keep updating the selected device status until the app is installed
            const interval = setInterval(() => {
                updateSelectedDevice(selectedGarmin).catch((e) => {
                    console.error("Could not update selected device", e);
                });
                setCheckCount(checkCount + 1);
            }, CHECK_INTERVAL);

            setIntervalHandle(interval);
        }

        if (
            selectedGarmin &&
            selectedGarmin.hasAppInstalled &&
            selectedGarmin.isBTCompatible &&
            intervalHandle
        ) {
            console.log("[GarminWatchStatus] Clearing Interval A");
            // if the app becomes installed, clear the interval
            clearInterval(intervalHandle);
            setIntervalHandle(undefined);
        }

        return () => {
            if (intervalHandle) {
                console.log("[GarminWatchStatus] Clearing Interval B");
                // clear the interval when the component is unmounted
                clearInterval(intervalHandle);
                setIntervalHandle(undefined);
            }
        };
    }, [openedConnectIQ, selectedGarmin, intervalHandle]);

    useEffect(() => {
        setAppsInstalledRowConfig(appsInstalledRow());
        setWatchConnectedRowConfig(watchConnectedRow());
        setWatchAppInstalledRowConfig(watchAppInstalledRow());
    }, [
        selectedGarmin?.isConnected,
        selectedGarmin?.hasAppInstalled,
        garminWatches?.isConnectInstalled,
        checkCount,
        openedConnectIQ,
    ]);

    useEffect(() => {
        setWatchConnectedRowConfig(watchConnectedRow());
        setWatchAppInstalledRowConfig(watchAppInstalledRow());
    }, [appsInstalledRowConfig]);

    useEffect(() => {
        setWatchAppInstalledRowConfig(watchAppInstalledRow());
    }, [appsInstalledRowConfig, watchConnectedRowConfig, openedConnectIQ]);

    if (!device) {
        return null;
    }

    return (
        <IonCard color="transparent">
            <IonCardHeader>
                <IonCardTitle
                    style={{
                        fontSize: 20,
                    }}
                >
                    {t("help.onBoarding.checkingWatch")}
                </IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
                <IonList mode="ios" style={{ borderRadius: 10 }}>
                    <GarminWatchStatusRow config={appsInstalledRowConfig} />

                    <GarminWatchStatusRow config={watchConnectedRowConfig} />

                    <GarminWatchStatusRow config={watchAppInstalledRowConfig} />
                </IonList>

                {/*<pre>{JSON.stringify(garminWatches, null, 2)}</pre>*/}
                {/*<IonButton*/}
                {/*    onClick={() => {*/}
                {/*        if (!device) {*/}
                {/*            alert("no device");*/}
                {/*            return;*/}
                {/*        }*/}
                {/*        updateSelectedDevice(device).catch((e) => {*/}
                {/*            console.error("Error updating selected device", e);*/}
                {/*        });*/}
                {/*    }}*/}
                {/*>*/}
                {/*    Update Statuses*/}
                {/*</IonButton>*/}
            </IonCardContent>
        </IonCard>
    );
};
